import React, { useMemo, useState } from 'react';
import { Box, Flex, Text } from '@chakra-ui/react';
import { Button, FormFeedback, OutlineCard } from '@morressier/alembic';

import { FieldError, useForm } from 'react-hook-form';
import { ajvResolver } from '@hookform/resolvers/ajv';
import { generateValidationSchema } from 'src/utils/validation';
import { FieldLabel } from 'src/components/Field';
import { Editor } from '@tinymce/tinymce-react';
import { FormFieldText } from '.';
import _ from 'lodash';
import { getValidationTypedMessage } from '../../../FormField/common/errorMessages';
import {
  applyRichTextSchemaKeywords,
  RICH_TEXT_AJV_KEYWORDS
} from 'src/utils/richTextSchemaValidation';
import { trimLeadingEmptyLines } from 'src/utils/string';
import striptags from 'striptags';

interface Props {
  isOpen: boolean;
  onSubmit: (value: string) => void;
  onCancel: () => void;
  formField: FormFieldText;
  value?: string;
}

type ITextAreaFormState = {
  metadata?: {
    [key in 'title' | 'abstract']: string;
  };
};

const RichTextUpdateView: React.FC<Props> = props => {
  const { onSubmit, onCancel, formField, value } = props;
  const fieldName = formField.caseDataFields.main;

  const schema = applyRichTextSchemaKeywords(
    generateValidationSchema({
      [formField.name]: formField
    })
  );

  const methods = useForm({
    defaultValues: _.set({} as ITextAreaFormState, fieldName, value),
    resolver: ajvResolver(schema, { keywords: RICH_TEXT_AJV_KEYWORDS })
  });

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors }
  } = methods;
  const error = _.get(errors, fieldName) as FieldError;
  const apiKeyTinymce = import.meta.env.VITE_TINY_MCE_CLIENT_KEY;
  const selfHostedTinymce = import.meta.env.VITE_SELF_HOSTED_TINY_MCE;
  const maxWords = formField.params.validations?.maxWords;

  const [wordCount, setWordCount] = useState(0);

  const textValue = watch(fieldName);

  const handleEditorChange = (content: string, editor: any): void => {
    const plainText = editor.getContent({ format: 'text' }).trim();
    const words = plainText.split(/\s+/).filter(Boolean).length;
    setWordCount(words);

    const normalizedContent = trimLeadingEmptyLines(content);

    if (plainText.length <= 0) {
      setValue(fieldName, '', { shouldValidate: true });
      return;
    }

    methods.clearErrors(fieldName);

    setValue(fieldName, normalizedContent, {
      shouldValidate: true
    });
  };

  const handleEditorBlur = (_evt: any, editor: any): void => {
    const plainText = editor.getContent({ format: 'text' }).trim();
    if (plainText.length <= 0) return;

    const current = methods.getValues(fieldName);
    const trimmed = current
      ? trimLeadingEmptyLines(current)
          .replace(/^(<[a-z][a-z0-9]*>)(\s|&nbsp;)+/gi, '$1')
          .replace(/(\s|&nbsp;)+(<\/[a-z][a-z0-9]*>)/gi, '$2')
          .trim()
      : current;
    if (trimmed !== current) {
      setValue(fieldName, trimmed, { shouldValidate: true });
    }
  };

  const currentPlainText = striptags(textValue ?? '')
    .replace(/&nbsp;/gi, ' ')
    .trim();

  const displayedWordCount = useMemo(() => {
    if (wordCount > 0) return wordCount;

    return currentPlainText
      ? currentPlainText.split(/\s+/).filter(Boolean).length
      : 0;
  }, [currentPlainText, wordCount]);

  const maxWordsExceeded =
    typeof maxWords === 'number' && displayedWordCount > maxWords;

  const validationErrorMessage =
    Boolean(error) &&
    getValidationTypedMessage(
      formField.params.labels.main,
      formField.params.validations.kind,
      formField.params.validations,
      error
    );

  const errorMessage = maxWordsExceeded
    ? `${formField.params.labels.main} cannot exceed ${maxWords} words`
    : validationErrorMessage;

  return (
    <Flex flexDirection="column" gap={8}>
      <form
        onSubmit={handleSubmit(data => {
          // TODO: see if we can avoid typecasting
          const raw = _.get(data, fieldName) as string;
          // Trim trailing whitespace/&nbsp; from HTML so the backend receives
          // the same clean value the frontend AJV validator sees.
          const trimmed = raw
            ? trimLeadingEmptyLines(raw)
                .replace(/^(<[a-z][a-z0-9]*>)(\s|&nbsp;)+/gi, '$1')
                .replace(/(\s|&nbsp;)+(<\/[a-z][a-z0-9]*>)/gi, '$2')
                .trim()
            : raw;
          onSubmit(trimmed);
        })}
      >
        <OutlineCard borderless padding={2} width="100%">
          <Flex flexDirection="column" gap={2}>
            <Text textStyle="headingSection">{`Update ${formField.params.labels.main}`}</Text>
          </Flex>
          <Flex flexDirection="column" gap={2}>
            <Flex flexDirection="column" gap={6} alignItems="flex-start">
              <FieldLabel
                label={formField.params.labels.main}
                subText={formField.params.labels.subtitle}
                isRequired={formField.params.validations.required}
              />
              <Flex flex={1} width={'100%'} gap={4}>
                <Box width="100%">
                  <Box
                    className={error || maxWordsExceeded ? 'editorErrorContainer' : ''}
                    width="100%"
                  >
                    <Editor
                      {...(apiKeyTinymce
                        ? { apiKey: apiKeyTinymce }
                        : { tinymceScriptSrc: selfHostedTinymce })}
                      licenseKey={selfHostedTinymce ? 'gpl' : ''}
                      value={textValue}
                      {...register(fieldName)}
                      init={{
                        menubar: false,
                        statusbar: false,
                        paste_as_text: true,
                        plugins: ['math', 'autoresize', 'wordcount'],
                        toolbar: 'undo redo | formatselect | math',
                        entity_encoding: 'raw'
                      }}
                      onEditorChange={handleEditorChange}
                      onBlur={handleEditorBlur}
                    />
                  </Box>

                  {typeof maxWords === 'number' && (
                    <Text
                      mt={2}
                      textAlign="right"
                      color={maxWordsExceeded ? 'red.500' : 'gray.500'}
                    >
                      {displayedWordCount} / {maxWords} words
                    </Text>
                  )}

                  {errorMessage && (
                    <FormFeedback variant="error">
                      {errorMessage}
                    </FormFeedback>
                  )}
                </Box>
              </Flex>
            </Flex>
            <Flex gap={2} justifyContent="flex-end">
              <Button
                variant="secondary"
                onClick={onCancel}
                borderRadius="small"
              >
                Exit Without Saving
              </Button>
              <Button variant="primary" type="submit" borderRadius="small">
                Save Changes
              </Button>
            </Flex>
          </Flex>
        </OutlineCard>
      </form>
    </Flex>
  );
};

export default RichTextUpdateView;
